/**
 * ShortcutsCheatsheet — Modal mit allen globalen Keyboard-Shortcuts.
 * Spec: docs/design-spec.md §9.
 *
 * Triggered via globalem Hotkey `?` (siehe `ShortcutsCheatsheetProvider`).
 *
 * Beispiel:
 *   <ShortcutsCheatsheetProvider>
 *     <App />
 *   </ShortcutsCheatsheetProvider>
 *
 * Die Liste hier ist die *einzige* User-sichtbare Quelle aller Shortcuts —
 * sie spiegelt 1:1 das Spec §9 wider und ist auf Deutsch gehalten.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { KbdHint } from "./KbdHint";

export interface ShortcutsCheatsheetProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

interface ShortcutEntry {
  keys: string;
  label: string;
}

interface ShortcutGroup {
  title: string;
  items: ShortcutEntry[];
}

/**
 * Vollständige Shortcut-Liste laut Spec §9.
 * Linke Spalte: Navigation + Listen. Rechte Spalte: Aktionen + Dialoge + Approval.
 */
const LEFT_GROUPS: ShortcutGroup[] = [
  {
    title: "Navigation",
    items: [
      { keys: "cmd k", label: "Command-Palette öffnen" },
      { keys: "g d", label: "Dashboard" },
      { keys: "g i", label: "Inbox" },
      { keys: "g r", label: "Rechnungen" },
      { keys: "g w", label: "Workflows" },
      { keys: "g l", label: "Lieferanten" },
      { keys: "g p", label: "Payment-Runs" },
      { keys: "g a", label: "Analytics" },
      { keys: "g s", label: "Einstellungen" },
      { keys: "cmd shift p", label: "Workspace wechseln" },
      { keys: "[", label: "Sidebar einklappen" },
      { keys: "]", label: "Sidebar ausklappen" },
    ],
  },
  {
    title: "Listen & Tabellen",
    items: [
      { keys: "j", label: "Nächste Zeile" },
      { keys: "k", label: "Vorherige Zeile" },
      { keys: "enter", label: "Zeile öffnen" },
      { keys: "f", label: "Filter öffnen" },
      { keys: "esc", label: "Auswahl / Filter schließen" },
    ],
  },
];

const RIGHT_GROUPS: ShortcutGroup[] = [
  {
    title: "Aktionen",
    items: [
      { keys: "n", label: "Neuer Beleg / Upload" },
      { keys: "cmd enter", label: "Formular abschicken" },
      { keys: "cmd shift a", label: "Alle ausgewählten freigeben" },
    ],
  },
  {
    title: "Approval-Inbox",
    items: [
      { keys: "a", label: "Freigeben" },
      { keys: "r", label: "Ablehnen" },
      { keys: "i", label: "Info anfordern" },
      { keys: "d", label: "Delegieren" },
      { keys: "c", label: "Kommentar verfassen" },
    ],
  },
  {
    title: "Beleg-Vorschau",
    items: [
      { keys: "space", label: "Vergrößern / verkleinern" },
    ],
  },
  {
    title: "Hilfe",
    items: [
      { keys: "?", label: "Diese Übersicht öffnen" },
    ],
  },
];

function GroupBlock({ group }: { group: ShortcutGroup }) {
  return (
    <div className="space-y-2">
      <h4 className="text-tiny uppercase tracking-wider text-text-tertiary font-medium">
        {group.title}
      </h4>
      <ul className="space-y-1.5" role="list">
        {group.items.map((item) => (
          <li
            key={`${group.title}-${item.keys}`}
            className="flex items-center justify-between gap-3 py-1"
          >
            <span className="text-small text-text-secondary">{item.label}</span>
            <KbdHint keys={item.keys} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ShortcutsCheatsheet({ open, onOpenChange }: ShortcutsCheatsheetProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tastenkürzel</DialogTitle>
          <DialogDescription>
            Alle globalen Shortcuts auf einen Blick. Drücke <KbdHint keys="?" /> jederzeit,
            um diese Übersicht zu öffnen oder zu schließen.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div className="space-y-6">
            {LEFT_GROUPS.map((g) => (
              <GroupBlock key={g.title} group={g} />
            ))}
          </div>
          <div className="space-y-6">
            {RIGHT_GROUPS.map((g) => (
              <GroupBlock key={g.title} group={g} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Provider mit globalem Hotkey
// ---------------------------------------------------------------------------

interface ShortcutsCheatsheetContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
}

const ShortcutsCheatsheetContext = createContext<ShortcutsCheatsheetContextValue | undefined>(
  undefined,
);

export function ShortcutsCheatsheetProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useHotkeys(
    "shift+/",
    (e) => {
      // `?` = Shift+Slash auf US-Layouts. Fokus-Awareness: nicht in Inputs feuern.
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target && target.getAttribute?.("contenteditable") === "true") return;
      e.preventDefault();
      setOpen((prev) => !prev);
    },
    { enableOnContentEditable: false, enableOnFormTags: false },
    [],
  );

  const value = useMemo<ShortcutsCheatsheetContextValue>(() => ({ open, setOpen }), [open]);

  return (
    <ShortcutsCheatsheetContext.Provider value={value}>
      {children}
      <ShortcutsCheatsheet open={open} onOpenChange={setOpen} />
    </ShortcutsCheatsheetContext.Provider>
  );
}

export function useShortcutsCheatsheet() {
  const ctx = useContext(ShortcutsCheatsheetContext);
  if (!ctx) {
    return { open: false, setOpen: useCallback(() => {}, []) };
  }
  return ctx;
}
